import { useMemo, useEffect, useState } from "react";
import { useLocation, useParams } from "wouter";
import { motion, AnimatePresence } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import {
  Download,
  ReceiptIndianRupee,
  Calendar,
  ArrowLeft,
  Loader2,
  ShoppingBag,
  Hash,
  RefreshCcw,
  IndianRupee,
  Check,
  Phone,
  BadgeCheck,
} from "lucide-react";
import { jsPDF } from "jspdf";
import { db as firebaseDb } from "@/lib/firebase";
import { doc, getDoc } from "firebase/firestore";
// SOURCE OF TRUTH for discount / tax / rounding math. Per project rule, all bill
// calculations route through this — DO NOT hand-roll discount or GST logic here.
import { calculateInvoiceSummary } from "@/lib/billingMath";

export default function PublicInvoice() {
  const [location] = useLocation();
  const params = useParams<{ userId?: string; orderId?: string; shortId?: string }>();
  const userId = params.userId;
  const orderId = params.orderId;
  const shortId = params.shortId;
  const [data, setData] = useState<{
    transactions: any[];
    createdAt: string;
    businessProfile?: any;
    isGstEnabled?: boolean;
    gstin?: string;
    cin?: string;
    fssai?: string;
    invoiceFooter?: string;
    invoiceTerms?: string;
    orderId?: string;
    // Snapshotted, top-level verified flag written at publish time. Public surfaces
    // cannot read users/{uid}/verifications/business, so we gate the badge on this.
    verified?: boolean;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadSuccess, setDownloadSuccess] = useState(false);
  const [businessPhone, setBusinessPhone] = useState<string | null>(null);
  const [phoneVerified, setPhoneVerified] = useState<boolean | null>(null);

  // Full screen success overlay component
  const SuccessOverlay = () => (
    <AnimatePresence>
      {downloadSuccess && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-center justify-center bg-[#991B1B]/90 backdrop-blur-sm"
        >
          <motion.div
            initial={{ scale: 0.5, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.5, opacity: 0 }}
            transition={{ type: "spring", damping: 15 }}
            className="flex flex-col items-center gap-6"
          >
            <div className="h-24 w-24 rounded-full bg-white flex items-center justify-center shadow-2xl">
              <Check className="h-12 w-12 text-[#991B1B]" strokeWidth={3} />
            </div>
            <div className="text-center">
              <h2 className="text-3xl font-black text-white mb-2">Success!</h2>
              <p className="text-white/80 font-bold">
                Invoice downloaded successfully
              </p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  useEffect(() => {
    if (!orderId && !shortId) {
      setLoading(false);
      return;
    }

    const fetchData = async () => {
      try {
        setLoading(true);
        setError(false);

        let realUserId = userId;
        let realOrderId = orderId;

        // NEW: Resolve the short link pointer first
        if (shortId) {
          const pointerRef = doc(firebaseDb, "public_invoices", shortId);
          const pointerSnap = await getDoc(pointerRef);

          if (pointerSnap.exists()) {
            const pData = pointerSnap.data();
            if (pData.isPointer) {
              realUserId = pData.userId;
              realOrderId = pData.orderId;
            } else {
              // Backward compatibility for old direct root invoices
              setData(pData as any);
              if (pData.businessProfile?.businessPhone) {
                setBusinessPhone(pData.businessProfile.businessPhone);
              }
              if (pData.businessProfile?.phoneVerified !== undefined) {
                setPhoneVerified(pData.businessProfile.phoneVerified);
              }
              setLoading(false);
              return;
            }
          } else {
            setError(true);
            setLoading(false);
            return;
          }
        }

        if (!realOrderId) {
          setError(true);
          setLoading(false);
          return;
        }

        // Standard Fetch using the real IDs
        let found = false;
        if (realUserId) {
          const newRef = doc(firebaseDb, "users", realUserId, "public_invoices", realOrderId);
          const newSnap = await getDoc(newRef);
          if (newSnap.exists()) {
            const invoiceData = newSnap.data();
            setData(invoiceData as any);
            if (invoiceData.businessProfile?.businessPhone) {
              setBusinessPhone(invoiceData.businessProfile.businessPhone);
            }
            if (invoiceData.businessProfile?.phoneVerified !== undefined) {
              setPhoneVerified(invoiceData.businessProfile.phoneVerified);
            }
            found = true;
          }
        }

        if (!found) {
          const oldRef = doc(firebaseDb, "public_invoices", realOrderId);
          const oldSnap = await getDoc(oldRef);
          if (oldSnap.exists()) {
            const invoiceData = oldSnap.data();
            setData(invoiceData as any);
            if (invoiceData.businessProfile?.businessPhone) {
              setBusinessPhone(invoiceData.businessProfile.businessPhone);
            }
            if (invoiceData.businessProfile?.phoneVerified !== undefined) {
              setPhoneVerified(invoiceData.businessProfile.phoneVerified);
            }
            found = true;
          }
        }

        if (!found) {
          setError(true);
        }
      } catch (err) {
        console.error("Invoice fetch failed:", err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [orderId, userId, shortId]);

  const transactions = data?.transactions || [];
  const businessProfile = data?.businessProfile;
  const isGstEnabled = data?.isGstEnabled;
  const gstin = data?.gstin;
  const cin = data?.cin || businessProfile?.cin;
  const fssai = data?.fssai || businessProfile?.fssai;

  // Cancelled invoices: line items carry `cancelled: true`. Surface this prominently to anyone
  // viewing the public link so they don't treat the bill as a valid record of sale.
  const isCancelled = !!transactions.some((t: any) => t.cancelled);
  const cancelledAt: string | undefined = transactions.find((t: any) => t.cancelledAt)?.cancelledAt;

  // Single source of truth for discount / tax / rounding — everything below derives from this.
  // calculateInvoiceSummary handles: percentage vs flat discount, GST inclusive vs exclusive,
  // largest-remainder discount distribution, free-item zeroing, HSN aggregation, rounding.
  // Published transaction docs already carry hsn/gstRate/gstInclusive denormalized, so we pass [] for products.
  const summary = useMemo(
    () => calculateInvoiceSummary(transactions, []),
    [transactions],
  );

  const discountedMainItems = summary?.discountedItems ?? [];
  const total = summary?.netPayable ?? 0;             // net payable after discount + tax
  const discount = summary?.discountAmount ?? 0;       // resolved rupee amount (handles % correctly)
  const invoiceGrossTotal = summary?.invoiceGrossTotal ?? 0;
  const hsnSummary = summary?.hsnSummary ?? {};
  const totalCgst = Object.values(hsnSummary).reduce((sum, v) => sum + v.cgst, 0);
  const totalSgst = Object.values(hsnSummary).reduce((sum, v) => sum + v.sgst, 0);
  const mainItems = discountedMainItems; // kept for any downstream reference

  const taxableValue = Object.values(hsnSummary).reduce((sum, v) => sum + v.taxable, 0);

  const generateInvoice = () => {
    const businessLogo = businessProfile?.businessLogo;
    const invoiceTotal = total;

    const filteredSaleItems = mainItems;

    const pageWidth = 80;
    let estimatedHeight = 20;

    if (businessLogo) {
      estimatedHeight += 25;
    }

    estimatedHeight += 10;

    const docTemp = new jsPDF({ unit: "mm", format: [80, 200] });
    if (businessProfile?.businessAddress) {
      const addr = docTemp.splitTextToSize(
        businessProfile.businessAddress,
        pageWidth - 20,
      );
      estimatedHeight += addr.length * 4;
    }
    if (businessProfile?.businessPhone) estimatedHeight += 4;
    if (businessProfile?.businessEmail) estimatedHeight += 4;
    if (businessProfile?.businessWebsite) estimatedHeight += 4;
    if (isGstEnabled && gstin) estimatedHeight += 4;
    if (isGstEnabled && cin) estimatedHeight += 4;
    if (isGstEnabled && fssai) estimatedHeight += 4;

    estimatedHeight += 10;
    estimatedHeight += 10;
    // Per-item budget — slightly overestimated so long-named items that wrap
    // to a second name line don't push the footer off the page. Worst-case real
    // consumption is ~13mm (2-line name + HSN); typical is 9mm. Use 11 as the
    // middle ground that leaves a small safety margin.
    const perItem = isGstEnabled ? 11 : 8;
    estimatedHeight += filteredSaleItems.length * perItem;

    // GST Summary block — header + dashed lines + 4mm per HSN row.
    // Underestimating this is what cropped the footer on bills with many HSN codes.
    if (isGstEnabled) {
      const hsnRowCount = Object.keys(hsnSummary).length;
      estimatedHeight += 16 + hsnRowCount * 4 + 4; // header (~16mm) + rows + closing line (~4mm)
    }
    estimatedHeight += 30; // NET PAYABLE block + savings line

    // --- Dynamic Height for T&C and Footer ---
    if (data?.invoiceTerms) {
      const termsLines = docTemp.splitTextToSize(data.invoiceTerms, pageWidth - 10);
      estimatedHeight += (termsLines.length * 4) + 10;
    }

    const hasCustomFooter = data?.invoiceFooter && data.invoiceFooter.trim().length > 0;
    if (hasCustomFooter) {
      const footerLines = docTemp.splitTextToSize(data.invoiceFooter as string, pageWidth - 10);
      estimatedHeight += (footerLines.length * 4) + 10;
    } else {
      estimatedHeight += 15; // gap before footer (10) + "THANK YOU" (5). "Visit again soon" doesn't advance yPos.
    }

    estimatedHeight += 15; // Tail: gap (10) + "Generated by DailyHisaab" link typeface + small safety

    if (data?.verified === true) {
      estimatedHeight += 8; // DAILYHISAAB VERIFIED badge line — keep the drawn badge in sync
    }

    const doc = new jsPDF({
      unit: "mm",
      format: [80, estimatedHeight],
    });

    let yPos = 15;

    if (businessLogo) {
      try {
        const imgWidth = 20;
        const imgHeight = 20;
        doc.addImage(
          businessLogo,
          "JPEG",
          (pageWidth - imgWidth) / 2,
          yPos,
          imgWidth,
          imgHeight,
        );
        yPos += imgHeight + 5;
      } catch (e) {
        console.error("Failed to add logo to PDF:", e);
      }
    }

    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    const bName = (
      businessProfile?.businessName || "RETAIL STORE"
    ).toUpperCase();
    doc.text(bName, pageWidth / 2, yPos, { align: "center" });
    yPos += 6;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);

    if (businessProfile?.businessAddress) {
      const addr = doc.splitTextToSize(
        businessProfile.businessAddress,
        pageWidth - 20,
      );
      doc.text(addr, pageWidth / 2, yPos, { align: "center" });
      yPos += addr.length * 4;
    }

    if (businessProfile?.businessPhone) {
      doc.text(businessProfile.businessPhone, pageWidth / 2, yPos, {
        align: "center",
      });
      yPos += 4;
    }

    if (businessProfile?.businessEmail) {
      doc.text(businessProfile.businessEmail, pageWidth / 2, yPos, {
        align: "center",
      });
      yPos += 4;
    }

    if (businessProfile?.businessWebsite) {
      doc.text(businessProfile.businessWebsite, pageWidth / 2, yPos, {
        align: "center",
      });
      yPos += 4;
    }

    if (isGstEnabled && gstin) {
      doc.setFont("helvetica", "bold");
      doc.text(`GSTIN: ${gstin}`, pageWidth / 2, yPos, { align: "center" });
      yPos += 4;
      doc.setFont("helvetica", "normal");
    }

    if (isGstEnabled && cin) {
      doc.text(`CIN: ${cin}`, pageWidth / 2, yPos, { align: "center" });
      yPos += 4;
    }

    if (isGstEnabled && fssai) {
      doc.text(`FSSAI Lic No: ${fssai}`, pageWidth / 2, yPos, {
        align: "center",
      });
      yPos += 4;
    }

    yPos += 2;
    doc.setLineDashPattern([1, 1], 0);
    doc.line(5, yPos, pageWidth - 5, yPos);
    yPos += 6;

    doc.setFontSize(7);
    const createdAtData = data?.createdAt;
    let finalDate = new Date();

    if (createdAtData) {
      if (
        typeof createdAtData === "object" &&
        createdAtData !== null &&
        "seconds" in (createdAtData as any)
      ) {
        finalDate = new Date((createdAtData as any).seconds * 1000);
      } else {
        const d = new Date(createdAtData as any);
        if (!isNaN(d.getTime())) {
          finalDate = d;
        }
      }
    }

    doc.text(`DATE: ${finalDate.toLocaleDateString("en-GB")}`, 5, yPos);
    doc.text(
      `TIME: ${finalDate.toLocaleTimeString("en-US", { hour12: false })}`,
      pageWidth - 5,
      yPos,
      {
        align: "right",
      },
    );
    yPos += 4;

    // Clean invoice number for display
    let displayInvNumber = data?.orderId || orderId || shortId || "";

    if (displayInvNumber.includes("-V-")) {
      displayInvNumber = displayInvNumber.replace("-V-", "-");
    } else if (displayInvNumber.startsWith("V-")) {
      displayInvNumber = displayInvNumber.replace("V-", "");
    }
    if (displayInvNumber.startsWith("INV-V-")) {
      displayInvNumber = displayInvNumber.replace("INV-V-", "INV-");
    }

    if (!displayInvNumber.startsWith("INV-")) {
      displayInvNumber = `INV-${displayInvNumber.slice(-8) || Date.now().toString().slice(-8)}`;
    }

    if (isGstEnabled) {
      doc.text(`INVOICE: ${displayInvNumber.toUpperCase()}`, 5, yPos);
    } else {
      const orderNum = displayInvNumber.replace(/^INV-/i, "").toUpperCase();
      doc.text(`ORDER #: ${orderNum}`, 5, yPos);
    }
    yPos += 6;

    doc.line(5, yPos, pageWidth - 5, yPos);
    yPos += 6;

    doc.setFont("helvetica", "bold");
    doc.text("Qty Item", 5, yPos);
    doc.text("Unit Price", 55, yPos, { align: "right" });
    doc.text("Total Price", pageWidth - 5, yPos, { align: "right" });
    yPos += 4;

    doc.line(5, yPos, pageWidth - 5, yPos);
    yPos += 6;

    doc.setFont("helvetica", "normal");
    discountedMainItems.forEach((item) => {
      const name = (item.productName || item["Product Name"] || "ITEM").toUpperCase();
      const qty = item.quantity || item.Quantity || 1;
      const rate = item.visualRate;
      const itemTotal = item.visualTotal;
      const hsn = item.hsn;

      const splitName = doc.splitTextToSize(name, pageWidth - 42);
      doc.text(`${qty} ${splitName[0]}`, 5, yPos);

      let nameY = yPos;
      if (splitName.length > 1) {
        for (let i = 1; i < splitName.length; i++) {
          nameY += 4;
          doc.text(splitName[i], 10, nameY);
        }
      }

      if (isGstEnabled && hsn) {
        nameY += 3;
        doc.setFontSize(6);
        doc.text(`HSN: ${hsn}`, 10, nameY);
        doc.setFontSize(7);
      }

      const unitPrice = Number(rate).toFixed(2);
      const totalDisplay = itemTotal.toFixed(2);

      doc.text(unitPrice, 55, yPos, { align: "right" });
      doc.text(totalDisplay, pageWidth - 5, yPos, {
        align: "right",
      });

      yPos = Math.max(yPos, nameY) + 6;
    });

    yPos += 2;
    doc.line(5, yPos, pageWidth - 5, yPos);
    yPos += 8;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    if (discount > 0) {
      doc.text("TOTAL WITH DISCOUNT", 5, yPos);
      doc.text(`Rs. ${total.toFixed(2)}`, pageWidth - 5, yPos, {
        align: "right",
      });
      yPos += 6;

      doc.text("YOUR SAVINGS TODAY", 5, yPos);
      doc.text(`Rs. ${discount.toFixed(2)}`, pageWidth - 5, yPos, {
        align: "right",
      });
      yPos += 8;
    } else {
      doc.text("TOTAL", 5, yPos);
      doc.text(`Rs. ${total.toFixed(2)}`, pageWidth - 5, yPos, {
        align: "right",
      });
      yPos += 8;
    }

    if (isGstEnabled && Object.keys(hsnSummary).length > 0) {
      doc.setLineDashPattern([1, 1], 0);
      doc.line(5, yPos, pageWidth - 5, yPos);
      yPos += 4;
      doc.setFont("helvetica", "bold");
      doc.setFontSize(8);
      doc.text("GST SUMMARY", 5, yPos);
      yPos += 4;

      doc.setFontSize(6);
      doc.text("HSN", 5, yPos);
      doc.text("Taxable", 25, yPos);
      doc.text("CGST", 45, yPos);
      doc.text("SGST", 65, yPos);
      yPos += 2;
      doc.line(5, yPos, pageWidth - 5, yPos);
      yPos += 4;

      doc.setFont("helvetica", "normal");
      Object.entries(hsnSummary).forEach(([hsn, values]) => {
        doc.text(hsn, 5, yPos);
        doc.text(values.taxable.toFixed(2), 25, yPos);
        doc.text(values.cgst.toFixed(2), 45, yPos);
        doc.text(values.sgst.toFixed(2), 65, yPos);
        yPos += 4;
      });
      doc.line(5, yPos, pageWidth - 5, yPos);
      yPos += 6;
      doc.setFontSize(9);
      doc.setFont("helvetica", "bold");
    }

    doc.setFontSize(11);
    doc.text("NET PAYABLE", 5, yPos);
    doc.text(`Rs. ${invoiceTotal.toFixed(2)}`, pageWidth - 5, yPos, {
      align: "right"
    });

    // --- Terms & Conditions in PDF ---
    if (data?.invoiceTerms) {
      yPos += 12;
      doc.setFont("helvetica", "bold");
      doc.setFontSize(8);
      doc.text("TERMS & CONDITIONS:", 5, yPos);
      yPos += 4;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(7);
      const terms = doc.splitTextToSize(data.invoiceTerms, pageWidth - 10);
      doc.text(terms, 5, yPos);
      yPos += (terms.length * 4);
    }

    // --- Dynamic Footer Replacement (PDF) ---
    yPos += 10;
    doc.setFontSize(8);
    if (data?.invoiceFooter && data.invoiceFooter.trim().length > 0) {
      doc.setFont("helvetica", "italic");
      const footer = doc.splitTextToSize(`"${data.invoiceFooter}"`, pageWidth - 10);
      doc.text(footer, pageWidth / 2, yPos, { align: "center" });
      yPos += (footer.length * 4);
    } else {
      doc.setFont("helvetica", "normal");
      doc.text("THANK YOU FOR SHOPPING!", pageWidth / 2, yPos, { align: "center" });
      yPos += 5;
      doc.text("Visit again soon", pageWidth / 2, yPos, { align: "center" });
    }

    // Verified-business badge — centered black line immediately above the Generated-by
    // tail (matches the native-Android footer). Gated on the snapshotted public flag;
    // public surfaces cannot read the live verification doc.
    if (data?.verified === true) {
      yPos += 8;
      doc.setFont("helvetica", "bold");
      doc.setFontSize(8);
      doc.setTextColor(0, 0, 0); // Generated-by tail leaves color blue; force black here
      // keep in sync with the DAILYHISAAB VERIFIED badge
      doc.text("**** DAILYHISAAB VERIFIED ****", pageWidth / 2, yPos, {
        align: "center",
      });
      doc.setFont("helvetica", "normal"); // don't leave the Generated-by tail bold
    }

    yPos += 10;
    doc.setFontSize(7);
    doc.setTextColor(0, 0, 255);
    doc.textWithLink("Generated by DailyHisaab", pageWidth / 2, yPos, {
      url: "https://dailyhisaab.com",
      align: "center",
    });

    const pdfFileNameId = data?.orderId || orderId || shortId || "Bill";
    doc.save(`Invoice_${pdfFileNameId}.pdf`);

    setDownloading(false);
    setDownloadSuccess(true);
    setTimeout(() => setDownloadSuccess(false), 3000);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FFFBEB]">
        <Loader2 className="h-8 w-8 animate-spin text-[#991B1B]" />
      </div>
    );
  }

  if (error || (!orderId && !shortId) || transactions.length === 0) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-4 bg-[#FFFBEB]">
        <Logo size="lg" className="mb-6" />
        <Card className="w-full max-w-md text-center overflow-hidden shadow-2xl border-none rounded-3xl">
          <div className="bg-[#991B1B] p-10 flex flex-col items-center">
            <IndianRupee className="h-20 w-20 text-white/50 mb-4" />
            <h1 className="text-2xl font-bold text-white">Bill Not Found</h1>
          </div>
          <CardContent className="p-8 bg-white">
            <p className="text-slate-600 mb-8 text-lg">
              Syncing your bill... please wait. If it takes too long, call the
              business to resend it.
            </p>
            <div className="space-y-3">
              <Button
                className="w-full h-14 bg-[#991B1B] hover:bg-[#7f1616] text-white rounded-2xl text-lg font-bold"
                onClick={() => window.location.reload()}
              >
                <RefreshCcw className="h-5 w-5 mr-2" /> Refresh Page
              </Button>
              <Button
                data-testid="button-call-business"
                className="w-full h-14 border-2 border-[#991B1B] text-[#991B1B] bg-white hover:bg-[#991B1B]/5 rounded-2xl text-lg font-bold"
                onClick={() => {
                  if (businessPhone)
                    window.location.href = `tel:${businessPhone}`;
                }}
              >
                <Phone className="h-5 w-5 mr-2" />
                Call Business
                {phoneVerified !== null && (
                  <BadgeCheck
                    className={`h-5 w-5 ml-2 shrink-0 ${phoneVerified ? "text-[#991B1B]" : "text-slate-400"}`}
                  />
                )}
              </Button>
              <Button
                variant="ghost"
                className="w-full text-slate-500"
                onClick={() => (window.location.href = "/")}
              >
                Go to Home
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FFFBEB] pb-12">
      <SuccessOverlay />
      <div className="bg-[#991B1B] sticky top-0 z-10 shadow-md">
        <div className="max-w-xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <a href="/" aria-label="Go to DailyHisaab">
              <img
                src="/DailiHisaab%20logo%20icon%20with%20white%20outline%20copy.png"
                alt="DailyHisaab"
                className="h-10 w-10"
              />
            </a>

            <span className="text-white font-bold text-lg border-l border-white/20 ml-2 pl-3">
              {businessProfile?.businessName || "Invoice"}
            </span>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="text-white border border-white rounded-full bg-transparent hover:bg-white/10 transition-all duration-200 !border-white min-w-[110px]"
            disabled={downloading}
            onClick={() => {
              setDownloading(true);
              generateInvoice();
            }}
          >
            <AnimatePresence mode="wait">
              {downloadSuccess ? (
                <motion.div
                  key="success"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  className="flex items-center"
                >
                  <Check className="h-4 w-4 mr-2" /> Saved!
                </motion.div>
              ) : downloading ? (
                <motion.div
                  key="loading"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="flex items-center"
                >
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving...
                </motion.div>
              ) : (
                <motion.div
                  key="default"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="flex items-center"
                >
                  <Download className="h-4 w-4 mr-2" /> Download
                </motion.div>
              )}
            </AnimatePresence>
          </Button>
        </div>
      </div>

      {isCancelled && (
        <div className="max-w-xl mx-auto px-4 pt-4">
          <div className="rounded-2xl bg-rose-50 border border-rose-200 p-4 text-center shadow-sm">
            <p className="text-sm font-black uppercase tracking-widest text-rose-700">Cancelled Invoice</p>
            <p className="text-xs font-medium text-rose-600/80 mt-1">
              This invoice was cancelled
              {cancelledAt ? ` on ${new Date(cancelledAt).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}` : ""}.
              It does not represent a completed sale.
            </p>
          </div>
        </div>
      )}

      <main className="max-w-xl mx-auto p-4 mt-6">
        <Card className={`overflow-hidden border-none shadow-2xl rounded-[2.5rem] bg-white ${isCancelled ? "opacity-70" : ""}`}>
          <CardHeader className="bg-[#991B1B] text-white p-8 pb-14 relative">
            <div className="absolute top-8 right-8 flex flex-col items-end gap-2">
              <div className="bg-[#F59E0B] text-[#991B1B] px-4 py-1 rounded-full font-black text-xs shadow-lg">
                INVOICE
              </div>
              {businessProfile?.businessLogo && (
                <div className="h-12 w-12 rounded-xl bg-white/10 p-1 border border-white/20 backdrop-blur-sm overflow-hidden flex items-center justify-center shadow-lg">
                  <img
                    src={businessProfile.businessLogo}
                    alt="Logo"
                    className="max-h-full max-w-full object-contain rounded-lg"
                  />
                </div>
              )}
            </div>
            <div className="p-3 bg-white/10 w-fit rounded-2xl mb-4 border border-white/20 shadow-lg backdrop-blur-sm">
              <IndianRupee className="h-6 w-6 text-white" strokeWidth={3} />
            </div>
            <CardTitle className="text-4xl font-black">
              ₹{total.toFixed(2)}
            </CardTitle>
            <div className="flex flex-col">
              <p className="text-white/60 text-xs font-bold uppercase tracking-widest mt-1">
                Total Bill Amount
              </p>
              {isGstEnabled && gstin && (
                <p className="text-white font-bold text-xs mt-2">
                  GSTIN: {gstin}
                </p>
              )}
              {isGstEnabled && cin && (
                <p className="text-white font-bold text-xs mt-1 opacity-80">
                  CIN: {cin}
                </p>
              )}
              {isGstEnabled && fssai && (
                <p className="text-white font-bold text-xs mt-0.5 opacity-80">
                  FSSAI: {fssai}
                </p>
              )}
            </div>
          </CardHeader>

          <CardContent className="p-6 -mt-8 bg-white rounded-t-[3rem] space-y-8">
            <section className="grid grid-cols-2 gap-4 pt-4">
              <div className="bg-slate-50 p-4 rounded-2xl">
                <span className="text-xs font-bold text-slate-400 uppercase block mb-1">
                  Date
                </span>
                <p className="font-bold text-slate-700">
                  {(() => {
                    const d = data?.createdAt ? new Date(data.createdAt) : null;
                    if (d && !isNaN(d.getTime())) {
                      return `${d.toLocaleDateString("en-GB")} ${d.toLocaleTimeString("en-US", { hour12: false })}`;
                    }
                    return new Date().toLocaleDateString();
                  })()}
                </p>
              </div>
              <div className="bg-slate-50 p-4 rounded-2xl">
                <span className="text-xs font-bold text-slate-400 uppercase block mb-1">
                  Bill No
                </span>
                <p className="font-bold text-slate-700">
                  {(() => {
                    let displayId = data?.orderId || orderId || shortId || "";
                    if (displayId.includes("-V-")) {
                      displayId = displayId.replace("-V-", "-");
                    } else if (displayId.startsWith("V-")) {
                      displayId = displayId.replace("V-", "");
                    }
                    if (displayId.startsWith("INV-V-")) {
                      displayId = displayId.replace("INV-V-", "INV-");
                    }
                    if (!displayId.startsWith("INV-")) {
                      displayId = `INV-${displayId.slice(-8)}`;
                    }
                    if (isGstEnabled) {
                      return displayId.toUpperCase();
                    } else {
                      return displayId.replace(/^INV-/i, "").toUpperCase();
                    }
                  })()}
                </p>
              </div>
            </section>

            <div className="space-y-3">
              <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest ml-1">
                Items Summary
              </h3>
              {discountedMainItems.map((item: any, i: number) => {
                // Determine if we need to show tax explicitly on the item line
                const hasTax = isGstEnabled && (Number(item.cgst) > 0 || Number(item.sgst) > 0);
                const isExclusive = hasTax && Math.abs(Number(item.total) - (item.taxable + Number(item.cgst) + Number(item.sgst))) < 0.1;

                return (
                  <div
                    key={i}
                    className="flex justify-between items-center p-4 bg-slate-50 rounded-2xl border border-slate-100"
                  >
                    <div className="flex-1">
                      <p className="font-bold text-slate-800">
                        {item.productName || item["Product Name"]}
                      </p>
                      <div className="flex items-center gap-2">
                        <p className="text-xs text-slate-400 font-bold">
                          {item.quantity || item.Quantity} Unit × ₹
                          {Number(item.visualRate).toFixed(2)}
                        </p>
                        {isGstEnabled && item.hsn && (
                          <p className="text-xs bg-slate-200 px-1.5 py-0.5 rounded text-slate-600 font-bold">
                            HSN: {item.hsn}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-black text-slate-800">
                        ₹{Number(item.visualTotal).toFixed(2)}
                      </p>
                      {hasTax && (
                        <p className="text-[10px] text-slate-400 font-bold">
                          GST included
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {isGstEnabled && Object.keys(hsnSummary).length > 0 && (
              <div className="space-y-3 border-t border-slate-100 pt-6">
                <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest ml-1">
                  GST Tax Breakdown
                </h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs text-left">
                    <thead>
                      <tr className="text-slate-400 border-b border-slate-50">
                        <th className="pb-2 font-bold uppercase">HSN</th>
                        <th className="pb-2 font-bold uppercase">Taxable</th>
                        <th className="pb-2 font-bold uppercase">CGST</th>
                        <th className="pb-2 font-bold uppercase">SGST</th>
                      </tr>
                    </thead>
                    <tbody className="text-slate-600">
                      {Object.entries(hsnSummary).map(([hsn, values]) => (
                        <tr key={hsn} className="border-b border-slate-50/50">
                          <td className="py-2 font-medium">{hsn}</td>
                          <td className="py-2">₹{values.taxable.toFixed(2)}</td>
                          <td className="py-2">₹{values.cgst.toFixed(2)}</td>
                          <td className="py-2">₹{values.sgst.toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {isGstEnabled && (
              <div className="space-y-2 border-t border-slate-100 pt-4">
                <div className="flex justify-between text-xs font-bold text-slate-400 uppercase tracking-wider px-1">
                  <span>Taxable Value</span>
                  <span>{taxableValue.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-xs font-bold text-slate-400 uppercase tracking-wider px-1">
                  <span>CGST</span>
                  <span>{totalCgst.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-xs font-bold text-slate-400 uppercase tracking-wider px-1">
                  <span>SGST</span>
                  <span>{totalSgst.toFixed(2)}</span>
                </div>
              </div>
            )}

            {discount > 0 && (
              <div className="bg-green-50 p-4 rounded-2xl flex justify-between items-center border border-green-100">
                <span className="text-green-700 font-bold">
                  Special Discount
                </span>
                <span className="text-green-700 font-black">
                  -₹{discount.toFixed(2)}
                </span>
              </div>
            )}

            <div className="pt-4 border-t border-dashed border-slate-200">
              <div className="flex justify-between items-center text-xl font-black text-[#991B1B]">
                <span>Grand Total</span>
                <span>₹{total.toFixed(2)}</span>
              </div>
            </div>

            {/* Terms & Conditions - Web View */}
            {data?.invoiceTerms && (
              <div className="p-4 bg-slate-50 rounded-2xl border border-slate-100 mt-6">
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Terms & Conditions</p>
                <p className="text-xs text-slate-600 leading-relaxed whitespace-pre-wrap">{data.invoiceTerms}</p>
              </div>
            )}

            {/* Dynamic Footer Replacement (Web View) */}
            {data?.invoiceFooter && data.invoiceFooter.trim().length > 0 ? (
              <p className="text-center text-sm font-medium italic text-slate-500 mt-6 whitespace-pre-wrap">
                "{data.invoiceFooter}"
              </p>
            ) : (
              <div className="text-center mt-6">
                <p className="text-sm font-bold text-slate-700">THANK YOU FOR SHOPPING!</p>
                <p className="text-xs text-slate-500">Visit again soon</p>
              </div>
            )}

            {/* Verified-business badge — mirrors the native-Android receipt footer. Gated on the
                snapshotted public flag (public surfaces cannot read the live verification doc). */}
            {data?.verified === true && (
              <p className="text-center text-xs font-bold text-black mt-6">
                {/* keep in sync with the DAILYHISAAB VERIFIED badge */}
                {"**** DAILYHISAAB VERIFIED ****"}
              </p>
            )}

            <footer className="text-center pt-10 pb-4 opacity-40">
              <p className="text-xs font-bold uppercase tracking-tighter">
                Generated by DailyHisaab
              </p>
            </footer>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}